/* eslint-disable no-misleading-character-class */

/**
 * Common security validation for notes and text fields
 * Checks for injection patterns while allowing normal punctuation
 * @param {string} value - Input value to validate
 * @returns {boolean} - True if value is safe, false if dangerous
 */
const validateAgainstInjectionPatterns = (value) => {
  // Block RTL/LTR override characters that can hide malicious content (V14_3 fix)
  if (/[\u202A-\u202E\u2066-\u2069]/.test(value)) return false;

  // Normalize spaces to detect obfuscated tags (e.g., "< script >" -> "<script>")
  const normalized = value.replace(/\s+/g, '');

  // Block polyglot injection patterns (special chars + parentheses + angle brackets)
  if (/[!"#$%&]{3,}.*[()[\]{}].*[<>]/.test(value)) return false;

  // Block <> together (looks like HTML tag)
  if (/<>/.test(value)) return false;

  // Block < and > when combined with multiple suspicious patterns
  // This catches cases where < and > are on separate lines but still dangerous
  if (/</.test(value) && />/.test(value)) {
    // Count suspicious characters
    const suspiciousChars = (value.match(/[!"#$%&/()[\]{}=|]/g) || []).length;
    // If we have many suspicious chars (injection attempt), block it
    if (suspiciousChars >= 5) {
      return false; // Likely polyglot injection attempt
    }
  }

  // Block HTML tags with spaces (e.g., "< script >", "< img ", etc.)
  if (
    /<\s*script|<\s*img|<\s*svg|<\s*iframe|<\s*object|<\s*embed|<\s*link|<\s*meta|<\s*div|<\s*body|<\s*style/gi.test(
      value,
    )
  )
    return false;

  // Block closing tags with spaces (e.g., "< /script >")
  if (
    /<\s*\/\s*(script|img|svg|iframe|object|embed|link|meta|div|body|style)/gi.test(
      value,
    )
  )
    return false;

  // Block event handlers with spaces (e.g., "on error", "on click")
  // Check both with and without spaces
  if (
    /\bon\s+(click|load|error|focus|blur|change|submit|mouseover|mouseout|keydown|keyup)\s*=/gi.test(
      value,
    )
  )
    return false;

  // Block event handlers with parentheses (e.g., onerror(), onclick())
  if (
    /\bon\s*(click|load|error|focus|blur|change|submit|mouseover|mouseout|keydown|keyup)\s*\(/gi.test(
      value,
    )
  )
    return false;

  // Block dangerous script patterns in normalized string (without spaces)
  if (
    /<script|<img|<svg|<iframe|<object|<embed|<link|<meta|<div|<body|<style/gi.test(
      normalized,
    )
  )
    return false;

  // Block event handlers in normalized string
  if (
    /\bon(click|load|error|focus|blur|change|submit|mouseover|mouseout|keydown|keyup)[\s]*=/gi.test(
      normalized,
    )
  )
    return false;

  // Block javascript: protocol
  if (/javascript\s*:/gi.test(value)) return false;

  // Block data: protocol
  if (/data\s*:/gi.test(value)) return false;

  // Block vbscript: protocol
  if (/vbscript\s*:/gi.test(value)) return false;

  // Block alert, eval, document, window
  if (/alert\s*\(|eval\s*\(|document\s*\.|window\s*\./gi.test(value))
    return false;

  // Block SQL injection patterns
  if (/(\bOR\b|\bAND\b).*[='"]/gi.test(value)) return false;
  if (/UNION.*SELECT/gi.test(value)) return false;

  // Block command injection patterns
  if (/[;&|]\s*(rm|del|drop|exec|eval|system)\b/gi.test(value)) return false;

  // Block encoded XSS attempts (HTML entities and URL encoding)
  if (/&lt;|&gt;|&#60|&#62|&#x3c|&#x3e|%3C|%3E|\\x3c|\\x3e/gi.test(value))
    return false;

  return true;
};

const VALIDATION_TYPES = {
  name: {
    regex:
      /^[a-zA-Z\s-'\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/,
    minLength: 2,
    maxLength: 100,
    validate: (value) => {
      const trimmed = value.trim();

      if (trimmed.length < 2 || trimmed.length > 100) return false;

      if (/^[\s-']+$/.test(trimmed)) return false;

      if (/[@!%#$/]/.test(trimmed)) return false;

      // Block ALL types of numbers (V14_2 fix - per QA spec)
      // Western digits (0-9), Arabic-Indic digits (٠-٩), Extended Arabic-Indic digits (۰-۹)
      if (/[\d\u0660-\u0669\u06F0-\u06F9]/.test(trimmed)) return false;

      // Block Hebrew punctuation that can cause backend issues (V15_1 fix)
      if (/[\u05C0-\u05C3\u05F3\u05F4]/.test(trimmed)) return false;

      // Block repetitive letters (V15_3 fix)
      if (/(.)\1{4,}/.test(trimmed)) return false;

      // Block Arabic diacritics that cause backend issues
      // These are vowel marks and special characters that backend doesn't support
      if (/[\u064B-\u0652\u0670\u0640\u06D6-\u06ED]/.test(trimmed))
        return false;

      if (
        !/^[a-zA-Z\s-'\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/.test(
          trimmed,
        )
      ) {
        return false;
      }

      return true;
    },
    sanitize: (value) => value.trim(),
  },
  email: {
    regex: /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/,
    minLength: 5,
    maxLength: 255,
    validate: (value) => {
      // Trim leading/trailing spaces as per spec AC7
      const trimmed = value.trim();

      if (trimmed.length < 5 || trimmed.length > 255) return false;

      // Check for multiple @ symbols
      if ((trimmed.match(/@/g) || []).length !== 1) return false;

      // Check for starting/ending with dot or dash
      if (
        trimmed.startsWith('.') ||
        trimmed.startsWith('-') ||
        trimmed.endsWith('.') ||
        trimmed.endsWith('-')
      )
        return false;

      // Check domain has at least one dot
      const domain = trimmed.split('@')[1];
      if (!domain || !domain.includes('.')) return false;

      return /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/.test(trimmed);
    },
    sanitize: (value) => value.trim().toLowerCase(),
  },
  phone: {
    regex: /^[0-9\s]{6,15}$/,
    minLength: 6,
    maxLength: 15,
    validate: (value) => {
      // Only digits and spaces allowed (NO + because country code field has it)
      if (!/^[\d\s]+$/.test(value)) return false;

      // Extract only digits for length validation
      const digitsOnly = value.replace(/[^\d]/g, '');

      // Check length (6-15 digits)
      if (digitsOnly.length < 6 || digitsOnly.length > 15) return false;

      return true;
    },
    sanitize: (value) => {
      // Keep only digits and spaces
      let cleaned = value.replace(/[^\d\s]/g, '');
      // Normalize spaces
      cleaned = cleaned.replace(/\s+/g, ' ').trim();
      return cleaned;
    },
  },
  address: {
    regex:
      /^[a-zA-Z0-9\s-',.#/:º\u00B0\u2018\u2019\u200F\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0B80-\u0BFF\u0E00-\u0E7F\u0900-\u097F\u2010-\u2015\u3010-\u301F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/,
    minLength: 5,
    maxLength: 255,
    validate: (value) => {
      const trimmed = value.trim();

      if (trimmed.length < 5 || trimmed.length > 255) return false;

      if (/^[0-9\s-',.#/:\u2018\u2019]+$/.test(trimmed)) return false;
      if (/^[\s-',.#/:\u2018\u2019]+$/.test(trimmed)) return false;

      if (/[@!?$%^&*=]/.test(trimmed)) return false;

      return /^[a-zA-Z0-9\s-',.#/:º\u00B0\u2018\u2019\u200F\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0B80-\u0BFF\u0E00-\u0E7F\u0900-\u097F\u2010-\u2015\u3010-\u301F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/.test(
        trimmed,
      );
    },
    sanitize: (value) => value.trim(),
  },
  city: {
    regex:
      /^[a-zA-Z\s-'\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/,
    minLength: 2,
    maxLength: 100,
    validate: (value) => {
      const trimmed = value.trim();

      if (trimmed.length < 2 || trimmed.length > 100) return false;

      // Block numbers and dots (V14_1 fix)
      if (/[0-9.]/.test(trimmed)) return false;

      // Block Hebrew punctuation that can cause backend issues (V15_1 fix)
      if (/[\u05C0-\u05C3\u05F3\u05F4]/.test(trimmed)) return false;

      if (/[@!%]/.test(trimmed)) return false;

      if (/^[\s-']+$/.test(trimmed)) return false;

      return /^[a-zA-Z\s-'\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/.test(
        trimmed,
      );
    },
    sanitize: (value) => value.trim(),
  },
  state: {
    regex:
      /^[a-zA-Z\s-'\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/,
    maxLength: 255,
    sanitize: (value) => value.trim(),
  },
  postal_code: {
    regex:
      /^[a-zA-Z0-9\s-'\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/,
    minLength: 3,
    maxLength: 10,
    validate: (value) => {
      const trimmed = value.trim();

      // Check length
      if (trimmed.length < 3 || trimmed.length > 10) return false;

      // Check for unsupported symbols
      if (/[@!?$%&*/]/.test(trimmed)) return false;

      // Check if contains valid characters (including international)
      return /^[a-zA-Z0-9\s-'\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]+$/.test(
        trimmed,
      );
    },
    sanitize: (value) => value.trim().toUpperCase(),
  },
  notes: {
    regex:
      /^[a-zA-Z0-9\s-',.!?:;()%&|"#$/<>=\n\r\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]*$/,
    validate: (value) => {
      if (typeof value !== 'string') return true;
      return validateAgainstInjectionPatterns(value);
    },
    sanitize: (value) => value,
  },
  quantity: {
    regex: /^\d+$/,
    validate: (value) => {
      // Must be only digits
      if (!/^\d+$/.test(value)) return false;
      const num = parseInt(value, 10);
      return Number.isInteger(num) && num >= 0 && num <= 999;
    },
    sanitize: (value) => parseInt(value, 10),
  },
  referer: {
    regex: /^[a-zA-Z0-9\s\-',.!?:;()/:@]*$/,
    sanitize: (value) => value,
  },
  policy_name: {
    regex: /^[a-zA-Z0-9\s-',.!?:;()]*$/,
    sanitize: (value) => value,
  },
  text: {
    regex:
      /^[a-zA-Z0-9\s-',.!?:;()%&|"#$/<>=\n\r\u00C0-\u036F\u0370-\u03FF\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u4E00-\u9FFF\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\u3040-\u309F\u30A0-\u30FF\uFB1D-\uFB4F]*$/,
    minLength: 3,
    maxLength: 100,
    validate: (value) => {
      if (typeof value !== 'string') return true;

      const trimmed = value.trim();
      if (trimmed.length < 3 || trimmed.length > 100) return false;

      return validateAgainstInjectionPatterns(value);
    },
    sanitize: (value) => value.trim(),
  },
  time: {
    regex: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
    sanitize: (value) => value,
  },
};

const DEFAULT_FIELD_CONFIGS = {
  first_name: { type: 'name', required: true },
  last_name: { type: 'name', required: true },
  email: { type: 'email', required: true },
  phone: { type: 'phone', required: true },
  address: { type: 'address', required: true },
  city: { type: 'city', required: true },
  state: { type: 'state', required: false },
  postal_code: { type: 'postal_code', required: true },
  notes: { type: 'notes', required: false, allowEmpty: true },
  text: { type: 'text', required: false, allowEmpty: true },
  country: { type: 'required', required: true },
  acceptTerms: { type: 'required', required: true },
  booking_referer: { type: 'referer', required: false, allowEmpty: true },
  cancellation_policy_name: {
    type: 'policy_name',
    required: false,
    allowEmpty: true,
  },
  cancellation_policy_guarantee: {
    type: 'policy_name',
    required: false,
    allowEmpty: true,
  },
  addon_quantity: { type: 'quantity', required: true },
  additional_field: { type: 'text', required: false },
};

const hasDangerousPatterns = (input) => {
  if (typeof input !== 'string') return false;
  // Reset regex state by creating a new instance
  const pattern =
    /<[^>]*>|<[^>]*$|javascript:|on\w+\s*=|data:|vbscript:|alert\(|eval\(|document\.|window\./gi;
  return pattern.test(input);
};

const validateInput = (input, config) => {
  const { type, required = false, allowEmpty = false } = config;

  if (!input || (typeof input === 'string' && input.trim() === '')) {
    if (required && !allowEmpty) {
      return { isValid: false, sanitized: '' };
    }
    return { isValid: true, sanitized: '' };
  }

  // Skip dangerous pattern check for notes and text fields (they have their own validation)
  if (type !== 'notes' && type !== 'text' && hasDangerousPatterns(input)) {
    return { isValid: false, sanitized: '' };
  }

  // Special handling for email - validate BEFORE trimming to catch trailing spaces
  if (type === 'email') {
    const validationType = VALIDATION_TYPES[type];
    if (validationType && validationType.validate) {
      const isValid = validationType.validate(input);
      const sanitized = isValid ? validationType.sanitize(input.trim()) : '';

      return {
        isValid,
        sanitized: isValid ? sanitized : '',
      };
    }
  }

  const trimmed = typeof input === 'string' ? input.trim() : input;
  const validationType = VALIDATION_TYPES[type];

  if (!validationType) {
    return { isValid: true, sanitized: trimmed };
  }

  let isValid = true;
  let sanitized = trimmed;

  if (validationType.validate) {
    isValid = validationType.validate(type === 'email' ? input : trimmed);
  } else if (validationType.regex) {
    isValid = validationType.regex.test(trimmed);
  }

  if (isValid && validationType.sanitize) {
    sanitized = validationType.sanitize(trimmed);
  }

  return {
    isValid,
    sanitized: isValid ? sanitized : '',
  };
};

export const validateField = (fieldName, value, customConfig = null) => {
  const config = customConfig || DEFAULT_FIELD_CONFIGS[fieldName];

  if (!config) {
    return { isValid: true, sanitized: value };
  }

  return validateInput(value, config);
};

export const createValidationConfig = (customConfigs = {}) => {
  return { ...DEFAULT_FIELD_CONFIGS, ...customConfigs };
};

export const validateFieldWithConfig = (fieldName, value, configs = {}) => {
  const validationConfig = createValidationConfig(configs);
  const config = validationConfig[fieldName];

  if (!config) {
    return { isValid: true, sanitized: value };
  }

  return validateInput(value, config);
};

export const validateFormFields = (formData, configs = {}) => {
  const validationConfig = createValidationConfig(configs);
  const results = {};
  let isValid = true;

  Object.keys(formData).forEach((fieldName) => {
    const config = validationConfig[fieldName];
    if (config) {
      const result = validateFieldWithConfig(
        fieldName,
        formData[fieldName],
        configs,
      );
      results[fieldName] = result;
      if (!result.isValid) {
        isValid = false;
      }
    }
  });

  return { isValid, results };
};

export const validateName = (name) => validateField('name', name);
export const validateEmail = (email) => validateField('email', email);
export const validateQuantity = (quantity) =>
  validateField('addon_quantity', quantity);
export const validateBookingReferer = (referer) =>
  validateField('booking_referer', referer);
export const validateCancellationPolicyName = (name) =>
  validateField('cancellation_policy_name', name);
